import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, Fingerprint, Loader2, Package } from "lucide-react";
import { MaterialRequestCard } from "@/components/dwr/TaskMaterials";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORY_LABEL, DELAY_LABEL, STATUS_LABEL, fmtDate, minutesLabel, todayISO } from "@/lib/dwr";
import DeleteButton from "@/components/DeleteButton";

function Tile({ label, value, tone = "", hint }) {
  return (
    <Card className="p-4 rounded-xl border border-border">
      <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className={`font-display text-3xl font-semibold mt-1 tabular-nums ${tone}`}>{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </Card>
  );
}

function Bars({ data, labels, tone }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  if (!entries.length) return <p className="text-sm text-muted-foreground">Nothing here.</p>;
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-sm"><span>{labels[k] || k}</span><span className="tabular-nums font-medium">{v}</span></div>
          <div className="h-2 rounded-full bg-muted mt-1"><div className={`h-2 rounded-full ${tone}`} style={{ width: `${(v / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

const startOfWeek = () => {
  const d = new Date(`${todayISO()}T00:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const MATERIAL_APPROVERS = ["SuperAdmin", "Director", "Manager", "OfficeAdmin"];

// Who checked in at HQ on the day, who was late and by how much, who didn't.
function AttendancePanel({ a, staffTotal, day, onChanged }) {
  return (
    <Card className="p-5 rounded-xl border border-border" data-testid="attendance-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <h3 className="font-display text-base font-semibold flex items-center gap-2"><Fingerprint className="w-4 h-4 text-primary" /> Check-ins at HQ · {fmtDate(day)}</h3>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{a.checked_in} / {staffTotal}</span> in · {a.late.length} late · {a.checked_out} checked out
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-rose-600 mb-2">Late</p>
          {a.late.length === 0 ? <p className="text-sm text-emerald-700 dark:text-emerald-400">Nobody late.</p> : (
            <ul className="space-y-1.5">{a.late.map((p) => (
              <li key={p.id} className="text-sm flex justify-between gap-2"><span>{p.name}</span>
                <span className="tabular-nums text-rose-600">{p.check_in_time} · {minutesLabel(p.late_minutes)}</span></li>
            ))}</ul>
          )}
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Checked in</p>
          {a.present.length === 0 ? <p className="text-sm text-muted-foreground">Nobody yet.</p> : (
            <ul className="space-y-1.5">{a.present.map((p) => (
              <li key={p.id} className="text-sm flex justify-between items-center gap-2"><span>{p.name}</span>
                <span className="flex items-center gap-1">
                  <span className="tabular-nums text-muted-foreground">{p.check_in_time}{p.check_out_time ? ` – ${p.check_out_time}` : ""}</span>
                  <DeleteButton iconOnly endpoint={`/dwr/attendance/${p.attendance_id}`} what="check-in" onDeleted={onChanged} />
                </span></li>
            ))}</ul>
          )}
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-amber-600 mb-2">Not checked in</p>
          {a.not_checked_in.length === 0 ? <p className="text-sm text-emerald-700 dark:text-emerald-400">Everyone is in.</p> : (
            <ul className="space-y-1.5">{a.not_checked_in.map((p) => (
              <li key={p.id} className="text-sm flex justify-between gap-2"><span>{p.name}</span><span className="text-muted-foreground">{p.department}</span></li>
            ))}</ul>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function WorkDashboard() {
  const { user } = useAuth();
  const [day, setDay] = useState(todayISO());
  const [from, setFrom] = useState(todayISO());
  const [d, setD] = useState(null);
  const [requests, setRequests] = useState([]);
  const approves = MATERIAL_APPROVERS.includes(user?.role);
  const loadRequests = useCallback(() => {
    if (!approves) return;
    api.get("/dwr/material-requests", { params: { status: "pending" } }).then(({ data }) => setRequests(data.items)).catch(() => {});
  }, [approves]);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  const reload = useCallback(() => {
    api.get("/dwr/dashboard", { params: { date: day, date_from: from, date_to: day } })
      .then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  }, [day, from]);
  useEffect(() => { setD(null); reload(); }, [reload]);

  const preset = (start) => { setDay(todayISO()); setFrom(start); };
  const open = d ? Object.values(d.open_by_status).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-6" data-testid="work-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Work Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Reports for {fmtDate(day)}{from !== day ? ` · task activity since ${fmtDate(from)}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant={from === day ? "default" : "outline"} onClick={() => preset(todayISO())}>Today</Button>
          <Button size="sm" variant="outline" onClick={() => preset(startOfWeek())}>This week</Button>
          <Button size="sm" variant="outline" onClick={() => preset(todayISO().slice(0, 8) + "01")}>This month</Button>
          <Input type="date" value={day} max={todayISO()} className="w-auto"
            onChange={(e) => { if (e.target.value) { setDay(e.target.value); if (from > e.target.value) setFrom(e.target.value); } }} />
        </div>
      </div>

      {!d ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Reports submitted" value={`${d.reports_submitted} / ${d.staff_total}`}
              hint={`${d.reports_waiting_review} waiting for review`} />
            <Tile label="Not submitted" value={d.not_submitted.length} tone={d.not_submitted.length ? "text-amber-600" : ""} />
            <Tile label="Open tasks" value={open} hint={`${d.tasks_assigned} assigned · ${d.tasks_completed} completed in period`} />
            <Tile label="Overdue / delayed" value={`${d.overdue} / ${d.open_by_status.delayed || 0}`}
              tone={d.overdue || d.open_by_status.delayed ? "text-rose-600" : ""} />
          </div>

          {d.attendance && <AttendancePanel a={d.attendance} staffTotal={d.staff_total} day={day} onChanged={reload} />}

          {requests.length > 0 && (
            <Card className="p-5 rounded-xl border border-amber-500/40">
              <h3 className="font-display text-base font-semibold mb-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-amber-600" /> Material changes to approve ({requests.length})
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {requests.map((r) => (
                  <div key={r.id} className="space-y-1">
                    <p className="text-sm font-medium">{r.task_title}{r.customer ? ` · ${r.customer}` : ""}</p>
                    <MaterialRequestCard request={r} onDecided={loadRequests} />
                  </div>
                ))}
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-5 rounded-xl border border-border">
              <h3 className="font-display text-base font-semibold mb-3">Not submitted ({fmtDate(day)})</h3>
              {d.not_submitted.length === 0 ? <p className="text-sm text-emerald-700 dark:text-emerald-400">Everyone has reported.</p> : (
                <ul className="space-y-1.5">{d.not_submitted.map((p) => (
                  <li key={p.id} className="text-sm flex justify-between gap-2"><span>{p.name}</span><span className="text-muted-foreground">{p.department}</span></li>
                ))}</ul>
              )}
            </Card>
            <Card className="p-5 rounded-xl border border-border">
              <h3 className="font-display text-base font-semibold mb-1">What's causing delays</h3>
              <p className="text-xs text-muted-foreground mb-3">Tasks currently delayed, by reason</p>
              <Bars data={d.delayed_by_reason} labels={DELAY_LABEL} tone="bg-rose-500" />
            </Card>
            <Card className="p-5 rounded-xl border border-border">
              <h3 className="font-display text-base font-semibold mb-1">Open work by status</h3>
              <p className="text-xs text-muted-foreground mb-3">Everything not yet completed or cancelled</p>
              <Bars data={d.open_by_status} labels={STATUS_LABEL} tone="bg-primary" />
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-5 rounded-xl border border-border">
              <h3 className="font-display text-base font-semibold mb-1">Completed in period</h3>
              <p className="text-xs text-muted-foreground mb-3">Installations, maintenance, customer visits…</p>
              <Bars data={d.completed_by_category} labels={CATEGORY_LABEL} tone="bg-emerald-500" />
            </Card>
            <Card className="p-5 rounded-xl border border-border lg:col-span-2">
              <div className="flex justify-between items-baseline mb-3">
                <h3 className="font-display text-base font-semibold">Delayed tasks</h3>
                <Link to="/dwr/tasks" className="text-xs text-primary">All tasks →</Link>
              </div>
              {d.delayed_tasks.length === 0 ? <p className="text-sm text-muted-foreground">No delayed tasks.</p> : (
                <ul className="divide-y divide-border">{d.delayed_tasks.slice(0, 8).map((t) => (
                  <li key={t.id} className="py-2 text-sm">
                    <div className="flex justify-between gap-2"><span className="font-medium">{t.task_no} · {t.title}</span><span className="text-muted-foreground whitespace-nowrap">{t.assigned_to_name}</span></div>
                    <p className="text-xs text-rose-700 dark:text-rose-400 mt-0.5">{DELAY_LABEL[t.delay_reason] || "Delayed"} · next action: {t.delay_responsible}{t.deadline ? ` · due ${fmtDate(t.deadline)}` : ""}</p>
                  </li>
                ))}</ul>
              )}
            </Card>
          </div>

          <Card className="p-5 rounded-xl border border-border">
            <h3 className="font-display text-base font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" /> Follow-ups due by {fmtDate(day)}
            </h3>
            {d.follow_ups_due.length === 0 ? <p className="text-sm text-muted-foreground">No follow-ups due.</p> : (
              <ul className="divide-y divide-border">{d.follow_ups_due.slice(0, 20).map((f, i) => (
                <li key={i} className="py-2 text-sm flex flex-wrap justify-between gap-2">
                  <span>{f.title}{f.customer ? ` · ${f.customer}` : ""}</span>
                  <span className="text-muted-foreground">{f.staff_name} · {fmtDate(f.follow_up_date)}</span>
                </li>
              ))}</ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
